import { useMutation, useQuery, useQueryClient, useTRPC } from "@luckymenu/api-client/react";
import {
	type DailySummarySettings,
	dailySummaryCoversDayBefore,
	timeOfDaySchema,
} from "@luckymenu/core";
import { useCallback, useMemo } from "react";
import { useCurrentMembership } from "./use-current";
import { usePlanIncludes } from "./use-navigation";

/**
 * The owner's daily summary push (P11-T08), on More > This device in both
 * apps: on or off and the time, each saved at once and shown at once; a
 * refusal puts the last saved value back and sets `failed`. Only owners on
 * a plan with reports have it (`available`); for anyone else nothing is
 * asked of the server.
 */
export function useDailySummary() {
	const trpc = useTRPC();
	const queryClient = useQueryClient();
	const current = useCurrentMembership();
	const hasReports = usePlanIncludes("reports");
	const available = current.data?.role === "owner" && hasReports;
	const queryKey = useMemo(() => trpc.staff.reports.dailySummary.queryKey(), [trpc]);
	const query = useQuery({
		...trpc.staff.reports.dailySummary.queryOptions(),
		enabled: available,
	});
	const mutation = useMutation(
		trpc.staff.reports.setDailySummary.mutationOptions({
			onMutate: async (next) => {
				await queryClient.cancelQueries({ queryKey });
				const before = queryClient.getQueryData<DailySummarySettings>(queryKey);
				queryClient.setQueryData<DailySummarySettings>(queryKey, next);
				return { before };
			},
			onError: (_error, _next, context) => {
				const before = (context as { before?: DailySummarySettings } | undefined)?.before;
				if (before) queryClient.setQueryData(queryKey, before);
			},
			onSuccess: (saved) => {
				const mutationKey = trpc.staff.reports.setDailySummary.mutationKey();
				if (queryClient.isMutating({ mutationKey }) <= 1) queryClient.setQueryData(queryKey, saved);
			},
		}),
	);
	const settings = query.data ?? null;
	const mutate = mutation.mutate;
	const save = useCallback(
		(patch: Partial<DailySummarySettings>) => {
			const now = queryClient.getQueryData<DailySummarySettings>(queryKey);
			if (!now) return;
			const next = { ...now, ...patch };
			// A half typed time ("" from a time field being cleared) is not saved.
			if (!timeOfDaySchema.safeParse(next.time).success) return;
			mutate(next);
		},
		[mutate, queryClient, queryKey],
	);
	return {
		/** The caller is an owner on a plan with reports. */
		available,
		settings,
		/** For the hint under the time: whether that time sends the day before. */
		coversDayBefore: settings ? dailySummaryCoversDayBefore(settings.time) : false,
		failed: mutation.isError,
		setEnabled: useCallback((enabled: boolean) => save({ enabled }), [save]),
		setTime: useCallback((time: string) => save({ time }), [save]),
	};
}
